// Home Page: src/pages/Home.tsx
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";

const offerings = [
  {
    title: "StudyTunnel (Flagship)",
    tag: "Education",
    description:
      "AI-powered learning and assessment platform for students, teachers, and parents.",
    cta: { label: "Explore Products", to: "/products" },
  },
  {
    title: "Online Exam Platform",
    tag: "Assessments",
    description:
      "Secure, scalable exams and practice journeys you can embed into your website or app.",
    cta: { label: "View Services", to: "/services" },
  },
  {
    title: "AI Assistant (Agents) ",
    tag: "Productivity",
    description:
      "Knowledge Q&A, summaries, and workflow automation to reduce repetitive work for teams.",
    cta: { label: "Talk to us", to: "/contact-us" },
  },
];

const steps = [
  { title: "Discover", description: "Clarify goals, users, and success metrics." },
  { title: "Design", description: "UX + UI flows optimized for education experiences." },
  { title: "Build", description: "Modern web + cloud-native delivery with quality checks." },
  { title: "Scale", description: "Performance, monitoring, and continuous improvement." },
];

const Home = () => {
  // Inject Title and Open Graph / SEO Meta Tags dynamically for Meta Scraper and Search Engines
  React.useEffect(() => {
    // 1. Set Browser Tab & Social Card Title
    document.title = "BhamuIntelli Systems LLP | Owner and Creators of StudyTunnel";

    // Helper to safely update or append meta tags
    const setMetaTag = (attrName: string, attrValue: string, content: string) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // 2. Standard Search Engine Meta Description
    setMetaTag(
      "name",
      "description",
      "BhamuIntelli Systems LLP is an education software development company and parent entity of StudyTunnel.com, building scalable learning, assessment, and AI platforms."
    );

    // 3. Meta / WhatsApp / Social Sharing Open Graph Tags
    setMetaTag("property", "og:title", "BhamuIntelli Systems LLP | Flagship Product: StudyTunnel");
    setMetaTag(
      "property",
      "og:description",
      "Official website for BhamuIntelli Systems LLP. Creators and developers of StudyTunnel.com, online exam systems, and AI learning tools."
    );
    setMetaTag("property", "og:url", "https://bhamuintellisystems.com/");
    setMetaTag("property", "og:type", "website");
  }, []);

  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          py: { xs: 6, md: 9 },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          {/* Company Header + StudyTunnel Link */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={800}>
              Bhamu
              <Box
                component="span"
                sx={{
                  color: "error.main",
                  fontWeight: 900,
                  fontSize: "1.15em",
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                i
              </Box>
              ntelli Systems LLP
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Flagship product:{" "}
              <Box
                component="a"
                href="https://studytunnel.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontStyle: "italic",
                  textDecoration: "underline",
                }}
              >
                StudyTunnel.com
              </Box>
            </Typography>
          </Box>

          {/* Hero text + What we deliver aligned */}
          <Grid container spacing={4} alignItems="stretch">
            {/* Left: Hero */}
            <Grid item xs={12} md={7}>
              <Typography variant="h4" component="h1" fontWeight={900} gutterBottom>
                Building modern scalable software solutions
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
                We design and build web platforms for learning, assessments, and student success—powered
                by cloud-native architecture and practical AI.
              </Typography>

              {/* Horizontally aligned CTAs */}
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button variant="contained" size="large" component={RouterLink} to="/products">
                  Explore Products
                </Button>
                <Button variant="outlined" size="large" component={RouterLink} to="/services">
                  View Services
                </Button>
                <Button variant="outlined" size="large" component={RouterLink} to="/contact-us">
                  Talk to us
                </Button>
              </Stack>
            </Grid>

            {/* Right: What we deliver */}
            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    What we deliver
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={1.5}>
                    <Box>
                      <Typography fontWeight={700}>Education Platforms</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Practice journeys, tests, dashboards, and learner insights.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>Assessment Systems</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Secure exams, analytics, and scalable evaluation flows.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>AI Capabilities</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Recommendations, assistants, summaries, and smart search.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* OFFERINGS */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Focused solutions for education
          </Typography>
          <Typography color="text.secondary">
            Start with a product, or engage us to build your next platform.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {offerings.map((o) => (
            <Grid item xs={12} md={4} key={o.title}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    <Typography variant="h6" fontWeight={800}>
                      {o.title}
                    </Typography>
                    <Chip size="small" label={o.tag} />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {o.description}
                  </Typography>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button variant="contained" component={RouterLink} to={o.cta.to}>
                    {o.cta.label}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* PROCESS */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: "action.hover" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
              How we work
            </Typography>
            <Typography color="text.secondary">
              Clear steps, fast delivery, and engineering discipline.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {steps.map((s, idx) => (
              <Grid item xs={12} md={3} key={s.title}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Chip label={`Step ${idx + 1}`} size="small" sx={{ mb: 1.5 }} />
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                      {s.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Ready to build something meaningful?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Tell us what you’re building in education—we’ll propose an MVP plan and architecture.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
          >
            <Button variant="contained" size="large" component={RouterLink} to="/contact-us">
              Get Started
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/products">
              View Products
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default Home;